import { useCallback, useEffect, useRef, useState } from "react";
import { Form, Image, ProgressBar } from "react-bootstrap";
import { CommunicationService } from "@propertelligent/client-api";
import { GetImages } from "../../../../utils/GetImages";
import CustomTooltip from "../../../common/CustomTooltip";
import apiHelper from "../../../../utils/apiHelper";
import CustomInputs from "../../../common/Inputs";
import EditorToolBar from "../../../common/EditorToolBar";
import AddButtonWithArrow from "../../../firstTimeUserExperience/commonCom/AddButtonWithArrow";
import { useGeneratePdfContext } from "../../../../utils/context/GeneratePdfContext";
import { NotificationType, PubSub, TopicsEnum, } from "../../../../utils/pubsub";
import { validateEmail } from "../../../../utils/validateHelper";
import { useError } from '../../../../utils/context/ErrorContext'
import { debounce } from "lodash";
import { sendPersonalEmail, sendPersonalOutlookEmail, replyOutlookMessage, forwardOutlookMessage, searchOutlookContacts, getOutlookMessage, EmailProviderType, OUTLOOK_MAX_TOTAL_ATTACHMENT_BYTES } from "../personalEmailApi";
import { resolveReplyRecipients } from "../emailViewHelpers";
// Matches the generated client's single formData param shape for postApiGmailSendEmailMessage/
// ReplyEmailThread/ForwardEmailThread (all three share this shape, mirroring [FromForm] SendEmailRequest
// server-side, the same way DocumentService.postApiDocument already does for [FromForm] DocumentVM). Derived
// from the generated function itself rather than a hand-duplicated interface so this can't drift if the
// backend's SendEmailRequest shape ever changes again.
type SendEmailFormData = Parameters<typeof CommunicationService.postApiGmailSendEmailMessage>[0];

// Shared by handleSubmit (folding any leftover typed text into a recipient at send time) and
// commitRecipientFromInput (turning typed text into a chip as the user types, via Enter/blur).
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const Compose = ({
  emailId,
  replyId,
  setReplyId,
  getEmailMessages,
  replytype,
  forwardBody = undefined,
  compose,
  setcompose,
  // EmailProviderType.Gmail (default), .CustomPopSmtp, or .Office365. SMTP only ever hits the new-message
  // branch below — POP3 has no reply/forward entry point (see .claude/context/emails.md Rule 12's Phase 2
  // scope note), so replyId/emailId are never populated when providerType is CustomPopSmtp. Office365 now
  // supports reply/forward too (Phase 2, ShowOutlookMessage.tsx) alongside its existing plain-send path.
  providerType = EmailProviderType.Gmail,
  // Called alongside getEmailMessages on every successful send/reply/forward so the Inbox/Sent/Important
  // tab counts refresh immediately rather than waiting on the next navigation/login (bug/ui-bugfix-batch9
  // item 9). Optional -- callers that don't surface tab counts (none currently) simply omit it.
  onEmailCountChanged = undefined,
  // Connected mailbox address; see resolveReplyRecipients.
  connectedEmail = "",

}) => {
  const { errors, errorMessages, setErrors, setErrorMessages, clearErrors } = useError();
  const [to, setTo] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [subject, setSubject] = useState("");
  const bodyRef = useRef(null);
  const [suggetions, setSuggetions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const searchInputRef = useRef(null);
  const suggestionsListRef = useRef(null);
  const { filesWithProgress, setFilesWithProgress } = useGeneratePdfContext();
  // Guards against a second submit firing while one is already in flight (repeated clicks/Enter before
  // the first apiHelper call resolves) -- the actual cause of "multiple emails getting sent" on repeated
  // clicks (bug/ui-bugfix-batch9), not a missing debounce. Also drives the Send button's existing,
  // previously-unused `loading` affordance. The ref is the authoritative re-entrancy guard (read/set
  // synchronously in handleSubmit, independent of React's render timing); the state mirror is only for
  // re-rendering the Send button's disabled/loading props.
  const isSendingRef = useRef(false);
  const [isSending, setIsSending] = useState(false);
  const setSending = (value: boolean) => {
    isSendingRef.current = value;
    setIsSending(value);
  };

  const notify = useCallback((message: string) => {
    PubSub.publish(TopicsEnum.Notification.toString(), {
      type: NotificationType.Failure,
      message,
    });
  }, []);

  useEffect(() => {
    if (replytype === "forward" && forwardBody && bodyRef.current) {
      bodyRef.current.innerHTML = `<br><br>---------- Forwarded message ----------<br>${forwardBody}`;
    }
  }, [replytype, forwardBody]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if ( searchInputRef.current && !searchInputRef.current.contains(event.target) ) {
        setSuggetions([]);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => { document.removeEventListener("click", handleClickOutside); };
  }, []);


  const handleSearch = useCallback(
    debounce((query) => {
      if (providerType === EmailProviderType.Office365) {
        searchOutlookContacts(query)
          .then((results) => setSuggetions(results ?? []))
          .catch((error) => console.error("Outlook contact search error:", error));
        return;
      }
      apiHelper(
        CommunicationService.getApiGmailSearchContacts,
        { showNotification: false },
        query
      ).then((res) => {
          const allresults: any = res.data.success;
          setSuggetions(allresults?.results);
        }).catch((error) => { });
    }, 300),
    [providerType]
  );

  useEffect(() => {
    // contact suggestions: Gmail People API and Outlook Graph People API both support search; custom SMTP
    // has no equivalent (POP3/SMTP protocols carry no contacts concept).
    if (!to || providerType === EmailProviderType.CustomPopSmtp) return;
    handleSearch(to);
  }, [to, providerType]);

  useEffect(() => {
    return () => {
      handleSearch.cancel();
    };
  }, []);

  useEffect(() => {
    if (!replyId) return;
    // Reply recipients are pre-filled as chips (not raw input text) so the contact-suggestion dropdown doesn't
    // open and echo the same address a second time.
    const prefillRecipients = (message) => {
      const emails = replytype === "reply" ? resolveReplyRecipients(message, connectedEmail) : [];
      setRecipients(emails.map((email) => ({ name: email, email })));
      setTo("");
      setSuggetions([]);
    };
    if (providerType === EmailProviderType.Office365) {
      getOutlookMessage(replyId)
        .then(prefillRecipients)
        .catch((error) => console.error("Failed to load Outlook message for reply/forward:", error));
      return;
    }
    apiHelper(CommunicationService.getApiGmailEmailMessage, { showNotification: false },
      replyId
    ).then((res) => prefillRecipients(res?.data?.success))
      .catch((error) => { });
  }, [replyId, replytype, providerType, connectedEmail]);

  const handleFileChange = async (e) => {
    const files = e.target.files;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setFilesWithProgress((prevFiles) => [
        ...prevFiles,
        { file, progress: 0 },
      ]);
    }
    const interval = setInterval(() => {
      setFilesWithProgress((prevFiles) =>
        prevFiles.map((file) => ({
          ...file,
          progress: Math.min(file.progress + 10, 100),
        }))
      );
    }, 500);

    setTimeout(() => {
      clearInterval(interval);
      setFilesWithProgress((prevFiles) =>
        prevFiles.map((file) => ({
          ...file,
          progress: 100,
        }))
      );
    }, 5000);
  };

  // disableAttachments mirrors the prop passed to EditorToolBar below.
  const disableAttachments = providerType === EmailProviderType.CustomPopSmtp;

  // Pasting an image (Ctrl+V) is native contentEditable browser behavior: the browser embeds it directly as
  // an inline <img src="data:...;base64,..."> in the DOM -- this matches Gmail's own compose behavior (a
  // pasted image appears inline in the message, not as a separate attachment card below it), so it is left
  // alone here. The large-payload "Unable to send email" failure this used to cause is fixed at its actual
  // source instead: the send/reply/forward endpoints now accept the body as multipart form data rather than
  // binding it off the URL query string, so an inline image's base64 no longer has to fit in a URL.

  // Clicking an inline image in the body shows a small floating "Remove" control positioned over it, mirroring
  // Gmail's own click-an-inline-image affordance but deliberately simplified to just removal -- Gmail's own
  // Small/Best fit/Original size/Edit alt text options aren't needed here.
  const [selectedImage, setSelectedImage] = useState<HTMLImageElement | null>(null);
  const [imageRemovePos, setImageRemovePos] = useState<{ top: number; left: number } | null>(null);

  const handleBodyClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "IMG") {
      const img = target as HTMLImageElement;
      // Measure against the overlay's actual containing block (.bodyContainer, position-relative), not the
      // editable body itself -- .bodyContainer has its own padding-top, so using the body's rect placed the
      // overlay that many pixels above the image.
      const containerRect = (e.currentTarget.parentElement as HTMLElement).getBoundingClientRect();
      const imgRect = img.getBoundingClientRect();
      setSelectedImage(img);
      setImageRemovePos({
        top: imgRect.top - containerRect.top,
        left: imgRect.left - containerRect.left,
      });
    } else {
      setSelectedImage(null);
      setImageRemovePos(null);
    }
  };

  const handleRemoveInlineImage = () => {
    selectedImage?.remove();
    setSelectedImage(null);
    setImageRemovePos(null);
  };

useEffect(() => {
 if(replyId==null || compose==false){
  setFilesWithProgress([])
 }
}, [replyId ,compose])

  const handleDelete = (index) => {
    setFilesWithProgress((prevFiles) =>
      prevFiles.filter((file, i) => i !== index)
    );
  };

  const handleSubjectChange = (e) => {
    setSubject(e.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSendingRef.current) return; // already in flight -- ignore a second submit (bug/ui-bugfix-batch9 item 11)

    const resetForm = () => {
      setTo("");
      setSubject("");
      setRecipients([]);
      if (bodyRef.current) bodyRef.current.textContent = null;
      setFilesWithProgress([]);
      // The body was just cleared -- drop any inline-image "Remove" overlay so it can't linger over nothing.
      setSelectedImage(null);
      setImageRemovePos(null);
    };

    let allRecipients = [...recipients];

    if (to.trim()) {
      const email = to.trim();
      const alreadyExists = allRecipients.some((r) => r.email === email);

      if (EMAIL_REGEX.test(email) && !alreadyExists) {
        allRecipients.push({ name: email, email });
      } else {
        notify("Please enter a valid email address or select from suggestions");
        return;
      }
    }

    if (!subject || subject.trim() === "") {
      notify("Subject is required");
      return;
    }

    // Validation passed -- this submit is actually going out. Disable Send until it settles.
    setSending(true);

    const formData = new FormData();
    filesWithProgress.forEach(({ file }, index) => {
      const blob = new Blob([file], { type: file.type });
      formData.append(`Attachments[${index}]`, blob, file.name);
    });

    const attachments = Array.from(formData.values()).filter(
      (val) => val instanceof Blob
    );

    const toEmails = allRecipients.map((r) => r.email).join(",");

    const messageBody = bodyRef.current.innerHTML;

    // Send email wrapper
    const sendEmail = (apiFn, emailIdParam: string | undefined, threadIdParam: string | undefined) => {
      const sendEmailFormData: SendEmailFormData = {
        To: toEmails,
        Subject: subject,
        Body: messageBody,
        EmailId: emailIdParam,
        ThreadId: threadIdParam,
        Attachments: attachments as Blob[],
      };
      apiHelper(
        apiFn,
        {
          successMessage: "Email sent Successfully",
          failureMessage: "Unable to send email",
          showNotification: true,
        },
        sendEmailFormData
      )
        .then((res) => {
          // setReplyId(null);
          if (res?.data?.success) {
            resetForm();
            getEmailMessages?.();
            onEmailCountChanged?.();
          }
        })
        .catch((error) => {
          console.error("Email send error:", error);
        })
        .finally(() => setSending(false));
    };

    if (providerType === EmailProviderType.Office365) {
      const outlookAttachments = filesWithProgress.map(({ file }) => file as File);
      const totalAttachmentBytes = outlookAttachments.reduce((sum, file) => sum + file.size, 0);
      if (totalAttachmentBytes > OUTLOOK_MAX_TOTAL_ATTACHMENT_BYTES) {
        setSending(false);
        notify("Attachments for an Outlook account must be 3 MB or less in total.");
        return;
      }
      const onOutlookSuccess = () => {
        resetForm();
        getEmailMessages?.();
        onEmailCountChanged?.();
        setReplyId?.(null);
        PubSub.publish(TopicsEnum.Notification.toString(), {
          type: NotificationType.Success,
          message: "Email sent successfully",
        });
      };
      const onOutlookFailure = (error) => {
        console.error("Personal Outlook send error:", error);
        notify("Unable to send email");
      };

      const settleSending = () => setSending(false);
      if (!emailId) {
        sendPersonalOutlookEmail(toEmails, subject, messageBody, outlookAttachments).then(onOutlookSuccess).catch(onOutlookFailure).finally(settleSending);
      } else if (replytype === "reply") {
        replyOutlookMessage(emailId, messageBody, toEmails, outlookAttachments).then(onOutlookSuccess).catch(onOutlookFailure).finally(settleSending);
      } else {
        forwardOutlookMessage(emailId, toEmails, messageBody, outlookAttachments).then(onOutlookSuccess).catch(onOutlookFailure).finally(settleSending);
      }
    } else if (!emailId && providerType === EmailProviderType.CustomPopSmtp) {
      // Personal SMTP send — new message only, no attachments in this pass (see Rule 12 Phase 2 scope note).
      // The attachment control is hidden for this provider (see the EditorToolBar disableAttachments prop below),
      // but guard here too in case files were staged before the provider was known.
      if (filesWithProgress.length > 0) {
        setSending(false);
        notify("Attachments aren't supported for a custom SMTP/POP3 account yet. Remove them and try again.");
        return;
      }
      sendPersonalEmail(toEmails, subject, messageBody)
        .then(() => {
          resetForm();
          getEmailMessages?.();
          onEmailCountChanged?.();
          PubSub.publish(TopicsEnum.Notification.toString(), {
            type: NotificationType.Success,
            message: "Email sent successfully",
          });
        })
        .catch((error) => {
          console.error("Personal SMTP send error:", error);
          notify("Unable to send email");
        })
        .finally(() => setSending(false));
    } else if (!emailId) {
      sendEmail(CommunicationService.postApiGmailSendEmailMessage, emailId, undefined);
    } else if (replytype === "reply") {
      // sendEmail's 2nd/3rd args become the form's EmailId/ThreadId fields respectively --
      // emailId here is ShowEmail's own prop, which is always the thread's real Gmail thread id (it's what
      // getApiGmailEmailThread was opened with), while replyId is the specific clicked message's own id
      // (setReplyId(item.id)). The real thread id must go in ThreadId -- passing replyId there instead
      // sent a per-message id as ThreadId, which 404'd on Gmail's threads.get for any reply except the thread's
      // first message (whose own id happens to equal the thread id in Gmail's ID scheme, masking the bug).
      // ForwardEmailThread below already gets this right; mirror it here.
      sendEmail(CommunicationService.postApiGmailReplyEmailThread, replyId, emailId);
    } else {
      sendEmail(CommunicationService.postApiGmailForwardEmailThread, undefined, emailId);
    }
  };

  const handleUserSelect = (user) => {
    if (!recipients.some((r) => r.email === user.email)) {
      setRecipients((prev) => [...prev, user]);
    }
    setTo(""); // clear the input box
    setSuggetions([]);
  };

  const handleInputChange = (e) => {
    setTo(e.target.value);
  };

  // Turns the currently-typed "To" text into a recipient chip, the same way selecting an autocomplete
  // suggestion already does -- validates it's a real email address first (reusing handleSubmit's own check)
  // and no-ops on blank/invalid/duplicate input rather than silently dropping or chipping garbage text.
  const commitRecipientFromInput = () => {
    const email = to.trim();
    if (!email) return;
    if (!EMAIL_REGEX.test(email)) {
      notify("Please enter a valid email address or select from suggestions");
      return;
    }
    if (!recipients.some((r) => r.email === email)) {
      setRecipients((prev) => [...prev, { name: email, email }]);
    }
    setTo("");
    setSuggetions([]);
  };

  // Enter used to submit the whole form (send) immediately, since this input sits inside <Form
  // onSubmit={handleSubmit}> and a plain text <input> submits its enclosing form on Enter by default --
  // preventDefault stops that native submit so Enter here only ever chips the typed address instead.
  const handleToKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitRecipientFromInput();
    }
  };

  // Moving focus elsewhere (Subject, body, ...) also commits whatever's typed, same as pressing Enter --
  // skipped when focus is moving into the suggestions dropdown itself (relatedTarget). The dropdown's own
  // onMouseDown preventDefault is what actually stops a suggestion click from blurring the input at all; this
  // check is defense-in-depth should a suggestion ever become focusable.
  const handleToBlur = (e) => {
    const nextFocused = e.relatedTarget as Node | null;
    if (nextFocused && suggestionsListRef.current?.contains(nextFocused)) {
      return;
    }
    commitRecipientFromInput();
  };

  // Ctrl+Enter (Cmd+Enter on Mac) sends from anywhere in the compose form, the explicit alternative to the
  // Send button now that a plain Enter no longer submits (see handleToKeyDown and the Form's onKeyDown
  // below). Reuses handleSubmit directly -- it only ever calls .preventDefault() on whatever event it's
  // given, which a KeyboardEvent supports the same as the form SubmitEvent handleSubmit normally receives.
  const handleFormKeyDown = (e) => {
    if (e.key !== "Enter") return;
    if (e.ctrlKey || e.metaKey) {
      handleSubmit(e);
      return;
    }
    // A plain Enter inside any single-line text <input> (Subject; the "To" field's own handleToKeyDown
    // already preventDefault-ed there) would otherwise still submit the form natively -- block it here too
    // as a single shared guard, rather than wiring a one-off onKeyDown onto every text input individually.
    // contentEditable (the message body) and real submit buttons are left alone: Enter there means "new
    // line" / "activate the focused button", not "submit the form".
    const target = e.target as HTMLElement;
    if (target.tagName === "INPUT" && (target as HTMLInputElement).type === "text") {
      e.preventDefault();
    }
  };

  return (
    <>
      <Form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className="composeForm">
        <div className="composeHeader">
          <div className="emailContent ">
            <div className="ToSection">
              <p className="sub_heading fw-normal">To</p>
              <div className="multi-email-input"
                ref={searchInputRef}
                onClick={() => searchInputRef.current?.querySelector('input')?.focus()}
              >
                {recipients.map((user, index) => (
                  <div key={index} className="email-chip">
                    {/* A chip created by typing an address directly (commitRecipientFromInput) has name ===
                        email -- showing "address (address)" was pure redundancy. A chip from a resolved
                        contact (handleUserSelect) still has a real display name distinct from the address,
                        so that case keeps showing both, matching the Figma design's "Name" (no address) for
                        the common case while not losing the address entirely for the contact case. */}
                    <span>{user.name === user.email ? user.name : `${user.name} (${user.email})`}</span>
                    <img
                      src={GetImages.Blackcross}
                      onClick={() =>
                        setRecipients((prev) =>
                          prev.filter((_, i) => i !== index)
                        )
                      }
                      className="chip-close"
                      alt="remove"
                    />
                  </div>
                ))}
                <input
                  type="text"
                  value={to}
                  onChange={handleInputChange}
                  onKeyDown={handleToKeyDown}
                  onBlur={handleToBlur}
                  // placeholder="Type name or email"
                  className="email-input-field"
                />
              </div>
              {to.trim() !== "" && suggetions?.length > 0 && (
                <ul
                  ref={suggestionsListRef}
                  className="selectedChat z-index-1 top-100 position-absolute"
                  // The <li> suggestions aren't focusable, so a mousedown on one would otherwise blur the "To"
                  // input with relatedTarget === null and let handleToBlur commit the partial typed text first
                  // (spurious "invalid email" toast, or the list unmounting before the click lands). Keeping
                  // focus in the input means only the click's handleUserSelect runs.
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {suggetions?.map((user, index) => (
                    <li
                      key={index}
                      className="lists showMe justify-content-between"
                      onClick={() => handleUserSelect(user)}
                    >
                      {user.name && user.name !== user.email && <p>{user.name}</p>}
                      <p>{user.email}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="ToSection">
            <p className="sub_heading fw-normal">Subject</p>
            <CustomInputs
              myClassName="w-100 sub_heading fw-normal black customSubjectInput"
              type="text"
              value={subject}
              onChange={handleSubjectChange}
              required
              id="customInputBorder"
              maxLength={150}
            />
          </div>
        </div>
        <div className="bodyContainer position-relative">
          <div contentEditable={true} className="emailBody" ref={bodyRef} onClick={handleBodyClick} />
          {selectedImage && imageRemovePos && (
            <div
              className="inline-image-remove"
              style={{ top: imageRemovePos.top, left: imageRemovePos.left }}
              onClick={handleRemoveInlineImage}
            >
              Remove
            </div>
          )}
          {filesWithProgress.length > 0 && (
            <div className="file-upload-List-height">
              {filesWithProgress.map(({ file, progress }, index) => (
                <div
                  key={index}
                  className="file-upload-item">
                  <div className="d-flex flex-start w-100 gap-2 align-items-center">
                    {file.type.startsWith("image/") ? (
                      <Image
                        style={{ height: "20px", width: "20px" }}
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        thumbnail
                      />
                    ) : file.type === "application/pdf" ? (
                      <div style={{ height: "20px", width: "20px" }}>
                        <img
                          style={{ height: "100%", width: "100%" }}
                          src={GetImages.pdfImage}
                          alt="PDF File"
                        />
                      </div>
                    ) : file.type ===
                      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ? (
                      <div style={{ height: "20px", width: "20px" }}>
                        <a
                          href={URL.createObjectURL(file)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            style={{ height: "100%", width: "100%" }}
                            src={GetImages.DocIcon}
                            alt="Word Document"
                          />
                        </a>
                      </div>
                    ) : (
                      // Handle other file types or display a generic placeholder
                      <div style={{ height: "20px", width: "20px", background: "#ccc" }}>
                        <img
                          style={{ height: "100%", width: "100%" }}
                          src={GetImages.GrayFile}
                          alt="placeholder"
                        />
                      </div>
                    )}
                    <p className="sub_heading black fw-normal">{file.name}</p>
                  </div>
                  <div className="email-progress-container flex-end">
                    {progress > 0 && progress < 100 ? (
                      <div className="w-100">
                        <ProgressBar
                          now={progress}
                          label={`${progress}%`}
                          visuallyHidden
                        />
                      </div>
                    ) : (
                      <div className="w-100"></div>
                    )}
                    <CustomTooltip content="Remove Attachment" children={
                      <img
                        onClick={() => handleDelete(index)}
                        className="iconstyle10 clickable color-white-icon"
                        src={GetImages.Blackcross}
                        alt="Remove Attachment"
                      />
                    } />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="emailBottomSection">
          <div className="textVariation">
            <EditorToolBar
              editorRef={bodyRef}
              handleFileChange={handleFileChange}
              disableAttachments={disableAttachments}
              notify={notify}
            />
          </div>
          <div className="d-flex align-items-center gap-2 composeActions">
             <CustomTooltip children={ <img
                    onClick={() =>{ if(replyId){
                     setReplyId(null);
                     setFilesWithProgress([])
                    }else if(compose){
                      setFilesWithProgress([])
                      setcompose(false)
                    }
                      setFilesWithProgress([])}}
                    src={GetImages.BlackDelete}
                    alt={GetImages.BlackDelete}
                    className="iconstyle flex-end color-white-icon clickable"
                  />}
             content={"Discard"} />
            {/* {replyId  || compose ? ( */}
              {/* <CustomTooltip
                // id="tooltip-disabled"
                // placement="Discard"
                tooltip="custom-tooltip"
                Element={
                  <img
                    onClick={() =>{ if(replyId){
                     setReplyId(null);
                     setFilesWithProgress([])
                    }else if(compose){
                      setFilesWithProgress([])
                      setcompose(false)
                    }
                      
                      setFilesWithProgress([])
                      


                    }}
                    src={GetImages.BlackDelete}
                    alt={GetImages.BlackDelete}
                    className="iconstyle flex-end"
                  />
                }
              /> */}
            {/* // ) : (
            //   ""
            // )} */}
            {/* CustomTooltip (react-bootstrap's OverlayTrigger) clones its child to inject hover/focus
                listeners -- AddButtonWithArrow is a plain function component that doesn't spread unknown
                props onto its underlying <button>, so those listeners would silently never attach if it were
                the direct child. Wrap it in a plain <span>, a real DOM node that accepts them, instead. */}
            <CustomTooltip content="Send (Ctrl+Enter)">
              <span>
                <AddButtonWithArrow
                  buttonname="Send"
                  onClick={null}
                  disabled={isSending}
                  loading={isSending}
                />
              </span>
            </CustomTooltip>
          </div>
        </div>
      </Form>
    </>
  );
};

export default Compose;

